import { IBase } from "./Base";
import { IRelationship } from "./Relationship";

// Database types - the fundamental storage types
export enum StandardFieldDatabaseType {
  VARCHAR = "varchar",
  TEXT = "text",
  INTEGER = "integer",
  BIGINTEGER = "bigint",
  DECIMAL = "decimal",
  BOOLEAN = "boolean",
  DATE = "date",
  TIMESTAMP = "timestamp",
  JSON = "jsonb",
  RELATIONSHIP = "relationship",
  GEO = "geo",
}

export enum RelationshipFieldDatabaseType {
  ONE_TO_ONE = "one-to-one",
  ONE_TO_MANY = "one-to-many",
  MANY_TO_ONE = "many-to-one",
  MANY_TO_MANY = "many-to-many",
  DYNAMIC_REFERENCE = "dynamic-reference",
}

export enum FileFieldDatabaseType {
  FILE = "file",
}

export enum UserFieldDatabaseType {
  USER = "user",
}

export enum SecretFieldDatabaseType {
  SECRET = "secret",
}

export type FieldDatabaseType =
  | StandardFieldDatabaseType
  | RelationshipFieldDatabaseType
  | FileFieldDatabaseType
  | UserFieldDatabaseType
  | SecretFieldDatabaseType;

// Display types - how fields are shown in the UI
export enum StandardFieldDisplayType {
  INPUT = "input",
  SELECT = "select",
  ENTITY_SELECT = "entity-select",
  TEXTAREA = "textarea",
  RICH_TEXT = "rich-text",
  CHECKBOX = "checkbox",
  RADIO = "radio",
  SHORT_DATE = "short-date",
  LONG_DATE = "long-date",
  TIMESTAMP = "timestamp",
  JSON = "jsonb",
  RELATIONSHIP = "relationship",
  GEO = "geo",
}

export enum FileFieldDisplayType {
  FILE = "file",
}

export enum UserFieldDisplayType {
  USER = "user",
}

export enum RelationshipFieldDisplayType {
  RELATIONSHIP = "relationship",
  DYNAMIC_REFERENCE = "dynamic-reference",
}

export enum SecretFieldDisplayType {
  SECRET = "secret",
}

export type FieldDisplayType =
  | StandardFieldDisplayType
  | FileFieldDisplayType
  | RelationshipFieldDisplayType
  | UserFieldDisplayType
  | SecretFieldDisplayType;

// Field option types
export type FieldSelectOption = {
  value: string;
  label: string;
};

export type FieldDisplayAddonOptions = {
  addon_before?: string;
  addon_after?: string;
  placeholder?: string;
  masked?: boolean;
};

export type FieldOptions = {
  input: FieldDisplayAddonOptions | null;
  select:
    | (FieldDisplayAddonOptions & {
        multiple: boolean;
        options: FieldSelectOption[];
      })
    | null;
  textarea: {
    rows: number;
  } | null;
  rich_text: {
    rows: number;
    enable_markdown: boolean;
  } | null;
  file: {
    max_size: number;
    max_files: number;
    accept: string;
    public: boolean;
  } | null;
};

// Main field interfaces
export type Field = IBase & {
  entity_id: number;
  name: string;
  label?: string;
  database_type: FieldDatabaseType;
  display_type: FieldDisplayType;
  description?: string;
  default_value?: string;
  is_required: boolean;
  is_name_field: boolean;
  is_unique: boolean;
  is_immutable: boolean;
  is_indexed: boolean;
  relationship_id: number | null;
  relationship: IRelationship | null;
  is_searchable: boolean;
  publicly_searchable: boolean;
  options: FieldOptions | null;
  sort_order: number;
  width: number;
  create_db_column: boolean;
  is_system: boolean;
};

export type CreateField = {
  name: string;
  label?: string;
  database_type: FieldDatabaseType;
  display_type: FieldDisplayType;
  description?: string;
  default_value?: string;
  is_required: boolean;
  is_name_field: boolean;
  is_unique: boolean;
  is_immutable: boolean;
  is_searchable: boolean;
  publicly_searchable: boolean;
  is_indexed: boolean;
  relationship: {
    target_entity_id: number;
    target_field_name?: string;
    on_deletion: "CASCADE" | "SET NULL" | "SET DEFAULT" | "RESTRICT";
    auto_create_related_field?: boolean;
  } | null;
  options: FieldOptions | null;
  width: number;
};

export type UpdateField = {
  label?: string;
  database_type: FieldDatabaseType;
  display_type: FieldDisplayType;
  description?: string;
  default_value?: string;
  is_required: boolean;
  is_name_field: boolean;
  is_unique: boolean;
  is_immutable: boolean;
  is_searchable: boolean;
  publicly_searchable: boolean;
  is_indexed: boolean;
  relationship?: {
    on_deletion: "CASCADE" | "SET NULL" | "SET DEFAULT" | "RESTRICT";
  } | null;
  options: FieldOptions | null;
  sort_order: number;
  width: number;
};

// Field type groups for UI Organisation
export const FIELD_TYPE_GROUPS = {
  Standard: [
    { value: StandardFieldDatabaseType.VARCHAR, label: "Small text" },
    { value: StandardFieldDatabaseType.TEXT, label: "Large text" },
    { value: StandardFieldDatabaseType.INTEGER, label: "Integer" },
    { value: StandardFieldDatabaseType.BIGINTEGER, label: "Big Integer" },
    { value: StandardFieldDatabaseType.DECIMAL, label: "Decimal" },
    { value: StandardFieldDatabaseType.BOOLEAN, label: "Boolean" },
    { value: StandardFieldDatabaseType.DATE, label: "Date" },
    { value: StandardFieldDatabaseType.TIMESTAMP, label: "Timestamp" },
    { value: StandardFieldDatabaseType.JSON, label: "JSON" },
    { value: StandardFieldDatabaseType.GEO, label: "Geo" },
  ],
  Relationships: [
    { value: RelationshipFieldDatabaseType.ONE_TO_ONE, label: "One-to-One" },
    { value: RelationshipFieldDatabaseType.ONE_TO_MANY, label: "One-to-Many" },
    { value: RelationshipFieldDatabaseType.MANY_TO_ONE, label: "Many-to-One" },
    {
      value: RelationshipFieldDatabaseType.MANY_TO_MANY,
      label: "Many-to-Many",
    },
    {
      value: RelationshipFieldDatabaseType.DYNAMIC_REFERENCE,
      label: "Dynamic Reference",
    },
  ],
  Files: [{ value: FileFieldDatabaseType.FILE, label: "File Upload" }],
  Users: [{ value: UserFieldDatabaseType.USER, label: "User" }],
  Secrets: [{ value: SecretFieldDatabaseType.SECRET, label: "Secret" }],
} as const;

// Type guard utilities
export const isStandardFieldType = (
  type: string,
): type is StandardFieldDatabaseType => {
  return Object.values(StandardFieldDatabaseType).includes(
    type as StandardFieldDatabaseType,
  );
};

export const isRelationshipFieldType = (
  type: string,
): type is RelationshipFieldDatabaseType => {
  return (
    type !== RelationshipFieldDatabaseType.DYNAMIC_REFERENCE &&
    Object.values(RelationshipFieldDatabaseType).includes(
      type as RelationshipFieldDatabaseType,
    )
  );
};

export const isRelationshipOrFileOrUserFieldType = (type: string) => {
  return [
    RelationshipFieldDatabaseType.ONE_TO_ONE,
    RelationshipFieldDatabaseType.ONE_TO_MANY,
    RelationshipFieldDatabaseType.MANY_TO_ONE,
    RelationshipFieldDatabaseType.MANY_TO_MANY,
    FileFieldDatabaseType.FILE,
    UserFieldDatabaseType.USER,
  ].includes(type as any);
};

export const isDynamicReferenceFieldType = (
  type: string,
): type is RelationshipFieldDatabaseType => {
  return type === RelationshipFieldDatabaseType.DYNAMIC_REFERENCE;
};

export const isFileFieldType = (
  type: string,
): type is FileFieldDatabaseType => {
  return Object.values(FileFieldDatabaseType).includes(
    type as FileFieldDatabaseType,
  );
};
